import { Link } from "@tanstack/react-router";
import { Calendar, Settings, Split, Swords, Users } from "lucide-react";
import logoUrl from "@/shared/assets/laget-logo-orange-big.png";
import { Button } from "@/shared/components/ui/button";
import type { components } from "@/shared/lib/api.types";
import { canManageRoster } from "@/shared/lib/roles";

type Me = components["schemas"]["UserResponse"];

const navLinkClass =
  "flex items-center gap-2 rounded-md px-3 py-2 text-sm text-header-foreground/70 transition-colors hover:bg-foreground/10 hover:text-header-foreground";
const navLinkActiveClass = "bg-foreground/10 text-header-foreground";

// Inner content shared by the desktop sidebar (<aside>) and the mobile drawer (Sheet).
// onNavigate lets the drawer close itself when a link is tapped; the sidebar omits it.
export function AppNav({
  me,
  onLogout,
  isPending,
  onNavigate,
}: {
  me: Me;
  onLogout: () => void;
  isPending: boolean;
  onNavigate?: () => void;
}) {
  return (
    <>
      <div className="flex h-16 items-center justify-center border-b border-border px-3">
        <img src={logoUrl} alt="LAGET" className="h-14 w-auto" />
      </div>
      <nav className="flex flex-col gap-1 p-2">
        <Link
          to="/"
          activeOptions={{ exact: true }}
          className={navLinkClass}
          activeProps={{ className: navLinkActiveClass }}
          onClick={onNavigate}
        >
          <Calendar className="size-4" />
          Schema
        </Link>
        <Link
          to="/setup"
          className={navLinkClass}
          activeProps={{ className: navLinkActiveClass }}
          onClick={onNavigate}
        >
          <Swords className="size-4" />
          Setup
        </Link>
        {canManageRoster(me.permissionRole) && (
          <>
            <Link
              to="/splits"
              className={navLinkClass}
              activeProps={{ className: navLinkActiveClass }}
              onClick={onNavigate}
            >
              <Split className="size-4" />
              Splits
            </Link>
            <Link
              to="/roster"
              className={navLinkClass}
              activeProps={{ className: navLinkActiveClass }}
              onClick={onNavigate}
            >
              <Users className="size-4" />
              Roster
            </Link>
          </>
        )}
      </nav>
      <nav className="mt-auto flex flex-col gap-1 p-2">
        <Link
          to="/settings"
          className={navLinkClass}
          activeProps={{ className: navLinkActiveClass }}
          onClick={onNavigate}
        >
          <Settings className="size-4" />
          Inställningar
        </Link>
      </nav>
      <div className="flex flex-col gap-1 border-t border-border p-3">
        <span className="text-base font-medium">
          {me.displayName ?? me.username}
        </span>
        <Button
          variant="link"
          className="h-auto justify-start p-0 text-muted-foreground hover:text-header-foreground"
          onClick={onLogout}
          loading={isPending}
        >
          Logga ut
        </Button>
      </div>
    </>
  );
}
